"use client";

import * as React from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ESSENTIALS_BY_CATEGORY, ESSENTIAL_ITEM_NAMES } from "@/lib/inventoryEssentials";

interface InventoryItemNameComboboxProps {
  value: string;
  onChange: (itemName: string) => void;
  placeholder?: string;
  disabled?: boolean;
  id?: string;
}

export function InventoryItemNameCombobox({
  value,
  onChange,
  placeholder = "Search or type item name…",
  disabled,
  id,
}: InventoryItemNameComboboxProps) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");

  const trimmedSearch = search.trim().toLowerCase();
  const hasCustomQuery = trimmedSearch.length > 0;

  // Filter groups: show categories that have at least one matching item; show "Use custom" when search doesn't match any exact item
  const filteredGroups = React.useMemo(() => {
    if (!trimmedSearch) {
      return ESSENTIALS_BY_CATEGORY;
    }
    const out: { category: string; items: string[] }[] = [];
    for (const group of ESSENTIALS_BY_CATEGORY) {
      const matches = group.items.filter((item) =>
        item.toLowerCase().includes(trimmedSearch)
      );
      if (matches.length > 0) out.push({ category: group.category, items: matches });
    }
    return out;
  }, [trimmedSearch]);

  const exactMatch = React.useMemo(() => {
    if (!trimmedSearch) return false;
    return ESSENTIAL_ITEM_NAMES.some(
      (name) => name.toLowerCase() === trimmedSearch
    );
  }, [trimmedSearch]);

  const showUseCustom =
    hasCustomQuery &&
    !exactMatch &&
    filteredGroups.some((g) =>
      g.items.some((item) => item.toLowerCase() === trimmedSearch)
    ) === false;

  const handleSelect = (selected: string) => {
    const isCustomOption = selected.startsWith("__custom__:");
    const nameToStore = isCustomOption
      ? selected.slice("__custom__:".length)
      : selected;
    onChange(nameToStore);
    setOpen(false);
    setSearch("");
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "w-full justify-between font-normal",
            !value && "text-muted-foreground"
          )}
        >
          {value || placeholder}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] min-w-[280px] max-w-[400px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search essentials…"
            value={search}
            onValueChange={setSearch}
          />
          <CommandList className="max-h-[300px]">
            <CommandEmpty>
              {hasCustomQuery ? (
                <div className="py-2 text-center text-sm text-muted-foreground">
                  No match. Use custom value below.
                </div>
              ) : (
                "No results."
              )}
            </CommandEmpty>
            {showUseCustom && (
              <CommandGroup heading="Custom">
                <CommandItem
                  value={`__custom__:${search.trim()}`}
                  onSelect={() => handleSelect(`__custom__:${search.trim()}`)}
                >
                  <Check className={cn("mr-2 h-4 w-4", value === search.trim() ? "opacity-100" : "opacity-0")} />
                  Use &quot;{search.trim()}&quot;
                </CommandItem>
              </CommandGroup>
            )}
            {filteredGroups.map((group) => (
              <CommandGroup key={group.category} heading={group.category}>
                {group.items.map((item) => (
                  <CommandItem
                    key={item}
                    value={item}
                    onSelect={() => handleSelect(item)}
                  >
                    <Check className={cn("mr-2 h-4 w-4", value === item ? "opacity-100" : "opacity-0")} />
                    {item}
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
